import { useMemo, useState } from 'react'
import type { PersonaView, RemoteProspect, RemoteProspectCampaign } from '../../lib/adminClient'
import { PersonDetail } from './PersonDetail'
import { Pill, inputCls, selectCls } from './ui'

// The list on the left, the person on the right. The list answers one
// question per row: who is this, and what is the next thing to do. Views are
// the few ways the owner actually looks at it; everything else is search.

type View = 'todo' | 'email' | 'waiting' | 'unchecked' | 'held' | 'done' | 'all'
const VIEWS: Array<[View, string]> = [
  ['todo', 'To do'],
  ['email', 'Email or form'],
  ['waiting', 'Waiting'],
  ['unchecked', 'Facts not checked'],
  ['held', 'Held back'],
  ['done', 'Done'],
  ['all', 'All']
]

const inView = (p: RemoteProspect, v: View): boolean => {
  switch (v) {
    case 'todo':
      return p.plan.actionable && p.plan.step !== 'hold' && p.plan.step !== 'done'
    case 'email':
      return ((!!p.email && !!p.emailSource) || !!p.contactFormUrl) && p.plan.step !== 'hold' && p.plan.step !== 'done'
    case 'waiting':
      return p.plan.step === 'wait'
    case 'unchecked':
      return !p.checkedAt && p.plan.step !== 'done' && p.plan.step !== 'hold'
    case 'held':
      return p.plan.step === 'hold'
    case 'done':
      return p.plan.step === 'done'
    case 'all':
      return true
  }
}

// Most urgent first: someone waiting on us, then a published address (email
// spends none of LinkedIn's few free notes), then the rest. Score breaks ties.
const STEP_ORDER: Record<string, number> = { reply: 0, message: 1, email: 2, form: 2, follow_up: 3, verify: 4, connect: 5, start_there: 6, no_route: 7, wait: 8, hold: 9, done: 10 }

const STEP_TONE: Record<string, 'accent' | 'warn' | 'good' | 'muted' | 'bad'> = {
  reply: 'good',
  message: 'accent',
  connect: 'accent',
  start_there: 'accent',
  email: 'accent',
  form: 'accent',
  follow_up: 'accent',
  verify: 'warn',
  no_route: 'warn',
  wait: 'muted',
  hold: 'bad',
  done: 'muted'
}

export function People({
  people,
  market,
  onClearMarket,
  campaigns,
  personas,
  selected,
  onSelect,
  onChanged,
  onDeleted
}: {
  people: RemoteProspect[]
  /** Set when a market was opened from the Markets tab: show only that one. */
  market?: string | null
  onClearMarket?: () => void
  campaigns: RemoteProspectCampaign[]
  personas: PersonaView[]
  selected: string | null
  onSelect: (id: string | null) => void
  onChanged: (p: RemoteProspect) => void
  onDeleted: (id: string) => void
}): JSX.Element {
  const [view, setView] = useState<View>('todo')
  const [q, setQ] = useState('')
  const [persona, setPersona] = useState('')
  const [tier, setTier] = useState('')

  // A market opened from the Markets tab narrows everything below, including
  // the view counts, so the numbers on the tabs describe the market you are
  // looking at rather than the whole list.
  const inMarket = (p: RemoteProspect): boolean =>
    !market || (market === 'No market recorded' ? !p.segment : p.segment === market)

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return people.filter(
      (p) =>
        inMarket(p) &&
        inView(p, view) &&
        (!persona || (persona === 'none' ? !p.personaId : p.personaId === persona)) &&
        (!tier || p.tier === tier) &&
        (!needle || [p.name, p.company, p.role, p.segment, p.personaName, p.hook].some((x) => (x ?? '').toLowerCase().includes(needle)))
    ).sort((a, b) => (STEP_ORDER[a.plan.step] ?? 99) - (STEP_ORDER[b.plan.step] ?? 99) || (b.score ?? -1) - (a.score ?? -1))
  }, [people, view, q, persona, tier, market])
  const count = (v: View): number => people.filter((p) => inMarket(p) && inView(p, v)).length

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(20rem,26rem)_1fr] gap-4 items-start">
      <div className="xl:col-span-2">
        {market && (
          <div
            className="glass-card rounded-xl px-4 py-2.5 mb-3 flex flex-wrap items-center gap-3 border border-accent/30"
            data-testid="people-market-banner"
          >
            <span className="text-sm text-slate-200">
              Working <span className="font-medium">{market}</span>
            </span>
            <span className="text-xs text-slate-500">
              {people.filter(inMarket).length} in this market · everything below is just them
            </span>
            <button
              className="ml-auto text-xs text-slate-400 hover:text-white"
              onClick={onClearMarket}
              data-testid="people-market-clear"
            >
              Show every market
            </button>
          </div>
        )}
      </div>
      <div className="glass-card rounded-xl overflow-hidden xl:sticky xl:top-4" data-testid="people-list">
        <div className="p-3 space-y-2 border-b border-white/5">
          <div className="flex flex-wrap gap-1">
            {VIEWS.map(([v, label]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-2.5 py-1 rounded-md text-xs ${view === v ? 'bg-accent/15 text-white border border-accent/30' : 'text-slate-400 hover:text-white border border-transparent'}`}
                data-testid={`view-${v}`}
              >
                {label} <span className="text-slate-500">{count(v)}</span>
              </button>
            ))}
          </div>
          <input className={inputCls} placeholder="Search name, company, niche" value={q} onChange={(e) => setQ(e.target.value)} data-testid="people-search" />
          <div className="flex gap-2">
            <select className={`${selectCls} flex-1`} value={persona} onChange={(e) => setPersona(e.target.value)} data-testid="people-persona">
              <option value="">Every persona</option>
              <option value="none">No persona yet</option>
              {personas.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
            <select className={selectCls} value={tier} onChange={(e) => setTier(e.target.value)}>
              <option value="">Any tier</option>
              <option value="A">Tier A</option>
              <option value="B">Tier B</option>
              <option value="C">Tier C</option>
            </select>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 px-3 py-1.5">
          {shown.length} of {people.length}
          {view === 'todo' ? ', most urgent first' : ''}
        </div>
        <ul className="max-h-[70vh] overflow-y-auto divide-y divide-white/5">
          {shown.length === 0 && <li className="px-3 py-6 text-sm text-slate-500 text-center">Nobody here.</li>}
          {shown.map((p) => (
            <li key={p.id}>
              <button
                onClick={() => onSelect(p.id)}
                className={`w-full text-left px-3 py-2.5 hover:bg-white/[0.03] ${selected === p.id ? 'bg-accent/10' : ''}`}
                data-testid={`person-row-${p.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate">{p.name}</span>
                  <span className="flex items-center gap-1.5 text-[10px] text-slate-500">
                    {p.email && p.emailSource && <span title={p.email} data-testid={`has-email-${p.id}`}>✉</span>}
                    {p.tier}
                  </span>
                </div>
                <div className="text-xs text-slate-500 truncate">{[p.company, p.personaName ?? p.segment].filter(Boolean).join(' · ')}</div>
                <div className="mt-1">
                  <Pill tone={STEP_TONE[p.plan.step] ?? 'muted'}>{p.plan.title}</Pill>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="min-w-0">
        {selected && people.some((p) => p.id === selected) ? (
          <PersonDetail
            id={selected}
            campaigns={campaigns}
            personas={personas}
            onChanged={onChanged}
            onDeleted={() => {
              onDeleted(selected)
              onSelect(null)
            }}
          />
        ) : (
          <div className="glass-card rounded-xl p-8 text-sm text-slate-400" data-testid="people-empty">
            Choose someone on the left. “To do” lists who has a next step today, people waiting on us first.
          </div>
        )}
      </div>
    </div>
  )
}
